import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import {
    Image,
    Flex,
    Spacer,
    TextButton,
} from '@zillow/constellation';

import { RMW_PATH } from 'app/shared/pathsManifest';

import ClaimActions from 'app/shared/actions/ClaimActions';

import numberUtils from 'app/shared/utils/numberUtils';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import ListingClaimUtils from 'app/shared/utils/listingClaimUtils';

import ManageClaimContactMethodsModal from 'app/shared/components/Modal/ManageClaimContactMethodsModal';
import ClaimListingCard from 'app/shared/components/ClaimListingCard';
import PropertyLookupModal from 'app/shared/components/Modal/PropertyLookupModal';
import stringHelper from 'app/shared/utils/stringHelper';
import WizardActionFooter from 'app/shared/components/WizardActionFooter';
import WizardBodyTemplate from 'app/shared/components/WizardBodyTemplate';
import HeartSearch from 'app/shared/images/HeartSearch.svg';
import NoListings from './components/NoListings';

import {
    ClaimListingCardContainer,
    StyledDesktopText,
    StyledMobileText,
    StyledContactMethodText,
} from './StyledComponents';

const { formatPhoneNumberInput } = numberUtils;
const { isEmailInContactMethods, isPhoneInContactMethods } = ListingClaimUtils;

const propTypes = {
    onGoToNextStep: PropTypes.func, // eslint-disable-line react/require-default-props
};

const WizFindListingResults = ({
    onGoToNextStep,
}) => {
    const dispatch = useDispatch();
    const initialRender = useRef(true);
    const [hasError, setHasError] = useState(false);
    const contactMethods = useSelector(state => state.claimListings.claimMethods);
    const contactMethodsCount = contactMethods.emails.length + contactMethods.phones.length;
    const showPropertyLookupModal = useSelector(state => state.claimListings.modals.showPropertyLookupModal);

    const [isTransitioning, setIsTransitioning] = useState(true);
    const [listings, setListings] = useState([]);
    const [showManageContactMethodsModal, setShowManageContactMethodsModal] = useState(false);
    const [isInitialRender, setIsInitialRender] = useState(true);
    const hasListings = listings.length > 0;

    const getAllData = () => {
        setIsTransitioning(true);
        Promise.all([
            dispatch(ClaimActions.propertyLookupAll({})),
            dispatch(ClaimActions.getClaimMethods()),
        ]).then((responses) => {
            setListings(responses[0].listings);
        }).catch(() => setHasError(true))
            .finally(() => setIsTransitioning(false));
    };

    useEffect(() => {
        getAllData();
        if (initialRender.current) {
            initialRender.current = false;
        } else {
            setIsInitialRender(false);
        }
    }, []);

    const handleContactMethodSuccessAdded = () => {
        dispatch(ClaimActions.setShowPropertyLookupModal(false));
        getAllData();
    };

    const handleContactMethodsUpdated = (contactHasBeenUpdated = false) => {
        setShowManageContactMethodsModal(false);
        if (contactHasBeenUpdated) {
            getAllData();
        }
    };

    const handleGoNextStep = () => {
        dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS.CLAIM_LISTINGS_CLICK_NEXT,
            { label: AnalyticsConstants.CLAIM_LISTINGS_LABEL.FIND_LISTING_RESULTS },
        ));
        onGoToNextStep();
    };

    const handleSearchForMoreListings = () => {
        dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS.CLAIM_LISTINGS_CLICK,
            {
                label: AnalyticsConstants.CLAIM_LISTINGS_LABEL.FIND_LISTING_RESULTS,
                action: 'HaveMoreProperties',
            },
        ));
        dispatch(ClaimActions.setShowPropertyLookupModal(true));
    };

    const handleManageContact = () => {
        dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS.CLAIM_LISTINGS_CLICK,
            {
                label: AnalyticsConstants.CLAIM_LISTINGS_LABEL.FIND_LISTING_RESULTS,
                action: 'ManageContactMethod',
            },
        ));
        setShowManageContactMethodsModal(true);
    };

    const handleLookupListingClick = () => {
        dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS.CLAIM_LISTINGS_CLICK,
            {
                label: AnalyticsConstants.CLAIM_LISTINGS_LABEL.FIND_LISTING_RESULTS,
                action: 'NoListingsListingLookup',
            },
        ));
        dispatch(ClaimActions.setShowPropertyLookupModal(true));
    };

    const renderSubtitle = () => {
        if (hasListings) {
            const email = contactMethods.emails[0] && contactMethods.emails[0].value;
            const phone = contactMethods.phones[0] && contactMethods.phones[0].value;
            const contactToRender = email || formatPhoneNumberInput(phone || '');
            const listingSubtitle = `We found ${stringHelper.pluralize(listings.length, 'listing', 'listings')}`;
            const contactMethodsSubtitle = contactMethodsCount > 1
                ? (
                    <>
                        {' '}
                        with
                        {' '}
                        <StyledContactMethodText>
                            {`${contactMethodsCount} contact methods`}
                        </StyledContactMethodText>
                    </>
                )
                : (
                    <>
                        {' '}
                        associated with
                        {' '}
                        <StyledContactMethodText>{contactToRender}</StyledContactMethodText>
                    </>
                );

            return (
                <>
                    {listingSubtitle}
                    {contactMethodsSubtitle}
                </>
            );
        }
        return `Transferring your listings to Zillow Rental Manager gives you more direct control over how your
        listings appear on the Zillow Rental Network and offers features like online applications, online lease
        signing and free virtual 3D tours.`;
    };

    const getToolTipText = () => {
        const initialRenderText = 'Listings are looked up by the contact information on the listing.';
        const initialMobileRenderText = 'Listings are looked up by the contact information on the listing.';

        const missingPropertiesText = (
            <>
                <b>Missing properties?</b>
                {' Try another contact method. If you\'re still having issues see'}
            </>
        );
        return (
            <>
                <StyledDesktopText fontType="smallbody">
                    {isInitialRender ? initialRenderText : missingPropertiesText}
                </StyledDesktopText>
                <StyledMobileText fontType="smallbody">
                    {isInitialRender ? initialMobileRenderText : missingPropertiesText}
                </StyledMobileText>
            </>
        );
    };

    const getToolTipHelpText = () => {
        // if (hasListings && !isInitialRender) {
        //     return 'Help';
        // }
        return '';
    };

    const handleLearnMoreClick = () => {
        window.open(RMW_PATH.LISTING_CLAIMING_HELP, '_blank');
    };

    return (
        <WizardBodyTemplate
            isTransitioning={isTransitioning}
            hasError={hasError}
            subtitle={renderSubtitle()}
            title={hasListings ? 'Your Listings' : 'Find your listings to transfer'}
            icon={hasListings ? null : <Image src={HeartSearch} />}
            tooltipHelpText={getToolTipHelpText()}
            tooltipText={getToolTipText()}
            onTooltipHelpClick={handleLearnMoreClick}
        >
            {hasListings ? (
                <>
                    {contactMethodsCount > 1 && (
                        <Spacer marginBottom="sm">
                            <Flex display="flex" justifyContent="center" alignItems="center">
                                <TextButton onClick={handleManageContact}>
                                    Manage contact methods
                                </TextButton>
                            </Flex>
                        </Spacer>
                    )}
                    <ClaimListingCardContainer>
                        {listings.map((listing, i) => (
                            <ClaimListingCard
                                key={`listing-card-${i}`}
                                listingData={listing}
                                highlightEmail={isEmailInContactMethods(contactMethods, listing.contactEmail)}
                                highlightPhone={isPhoneInContactMethods(contactMethods, listing.contactPhone)}
                            />
                        ))}
                    </ClaimListingCardContainer>
                </>
            ) : (
                <NoListings onLookUpListingsClick={handleLookupListingClick} />
            )}
            <WizardActionFooter
                hideBackButton={true}
                isNextDisabled={!hasListings}
                customActionLabel={hasListings ? 'Search for more listings' : null}
                onCustomActionClick={handleSearchForMoreListings}
                onNextClick={handleGoNextStep}
            />
            {showPropertyLookupModal && (
                <PropertyLookupModal
                    onContactMethodAddedSuccess={handleContactMethodSuccessAdded}
                    onClose={() => dispatch(ClaimActions.setShowPropertyLookupModal(false))}
                />
            )}
            {showManageContactMethodsModal && (
                <ManageClaimContactMethodsModal
                    onClose={handleContactMethodsUpdated}
                    contactMethods={contactMethods}
                />
            )}
        </WizardBodyTemplate>
    );
};

WizFindListingResults.propTypes = propTypes;

export default WizFindListingResults;
